
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { Copy, Check, ChevronDown, ChevronRight, Wallet, Shield, Bot, Key, Repeat, Send, Zap, Server, Lock, Globe, Code } from 'lucide-react';
import { Card } from '../../ui/Card';

interface Endpoint {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  description: string;
  params?: Record<string, string>;
  response: string;
}

interface Module {
  name: string;
  icon: any;
  endpoints: Endpoint[];
}

const CodeBlock = ({ code }: { code: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <div className="absolute right-3 top-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
        <button 
          onClick={handleCopy}
          className="p-2 rounded-lg bg-white/10 text-gray-400 hover:text-white transition-colors backdrop-blur-sm border border-white/10"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
      <pre className="bg-[#0A0B0F] text-gray-300 p-6 rounded-2xl text-[12px] font-mono overflow-x-auto border border-white/5 shadow-2xl">
        {code}
      </pre>
    </div>
  );
};

const EndpointCard: React.FC<{ endpoint: Endpoint }> = ({ endpoint }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getMethodColor = (method: string) => {
    switch(method) {
      case 'GET': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'POST': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'PUT': return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      case 'DELETE': return 'bg-red-500/10 text-red-500 border-red-500/20';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  return (
    <div className={`border rounded-2xl overflow-hidden mb-3 transition-all duration-300 ${isExpanded ? 'bg-stone-50 dark:bg-white/[0.02] border-stone-300 dark:border-white/20' : 'bg-white dark:bg-[#15171C] border-stone-200 dark:border-white/5 hover:border-nobel-gold/30'}`}>
       <button 
         onClick={() => setIsExpanded(!isExpanded)}
         className="w-full flex items-center justify-between p-4 text-left group"
       >
          <div className="flex items-center gap-4 overflow-hidden">
             <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border ${getMethodColor(endpoint.method)} min-w-[60px] text-center uppercase tracking-widest`}>
                {endpoint.method}
             </span>
             <span className="font-mono text-sm text-[#0A0B0F] dark:text-stone-200 truncate">{endpoint.path}</span>
             <span className="text-xs text-stone-500 hidden lg:block truncate border-l border-stone-200 dark:border-white/10 pl-4 ml-2">{endpoint.description}</span>
          </div>
          <div className="flex-shrink-0 ml-4">
            {isExpanded ? <ChevronDown size={18} className="text-stone-400"/> : <ChevronRight size={18} className="text-stone-400 group-hover:text-nobel-gold transition-colors"/>}
          </div>
       </button>
       
       {isExpanded && (
         <div className="p-6 border-t border-stone-200 dark:border-white/5 space-y-6 animate-fade-in">
            {endpoint.params && (
              <div>
                 <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-400 mb-3 ml-1">Request Body / Params</h4>
                 <div className="bg-white dark:bg-[#0A0B0F] rounded-xl border border-stone-200 dark:border-white/10 overflow-hidden shadow-inner">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="bg-stone-50 dark:bg-white/5 border-b border-stone-200 dark:border-white/10 text-stone-500 uppercase tracking-widest">
                                <th className="p-3 text-left">Key</th>
                                <th className="p-3 text-left">Description</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100 dark:divide-white/5 font-mono">
                            {Object.entries(endpoint.params).map(([key, desc]) => (
                                <tr key={key}>
                                    <td className="p-3 text-nobel-gold font-bold">{key}</td>
                                    <td className="p-3 text-stone-600 dark:text-stone-400">{desc}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                 </div>
              </div>
            )}
            <div>
               <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-400 mb-3 ml-1">Response JSON</h4>
               <CodeBlock code={endpoint.response} />
            </div>
         </div>
       )}
    </div>
  );
};

export const ApiReference: React.FC = () => {
  const modules: Module[] = [
    {
        name: "Authentication",
        icon: Key,
        endpoints: [
          {
            method: "POST", path: "/v1/auth/keys/generate", description: "Generate a Ghost Secret Key with specific rate limits and scopes.",
            params: { "name": "Key label", "rateLimit": "Daily request limit", "scopes": "['read:payments', 'write:escrow']" },
            response: `{\n  "id": "key_8921",\n  "key": "sk_live_51Mx82B... (Only shown once)",\n  "prefix": "sk_live_51Mx",\n  "status": "active"\n}`
          },
          {
            method: "POST", path: "/v1/auth/keys/rotate", description: "Invalidate old secret and issue a new high-entropy key.",
            params: { "key_id": "UUID of key" },
            response: `{\n  "success": true,\n  "newKey": "sk_live_rotated_..." \n}`
          }
        ]
    },
    {
      name: "Privacy & Batch Payments",
      icon: Send,
      endpoints: [
        {
          method: "POST", path: "/v1/payments/batch", description: "Execute multiple transfers in a single atomic transaction.",
          params: { "payments": "[{to, amount, token}]", "stealth": "bool (enable one-time addresses)" },
          response: `{\n  "status": "confirmed",\n  "txHash": "5s89...j29k",\n  "batchId": "bch_102",\n  "recipients": 12\n}`
        },
        {
          method: "POST", path: "/v1/payments/verify-zk", description: "Verify stealth payment metadata using Groth16 proofs.",
          params: { "proof": "Base64 ZK proof", "publicInputs": "Array of inputs" },
          response: `{\n  "valid": true,\n  "merchantMetadata": { "invoice": "INV-042" }\n}`
        }
      ]
    },
    {
      name: "Escrow Factory",
      icon: Shield,
      endpoints: [
        {
          method: "POST", path: "/v1/escrow/create", description: "Deploy a new cross-chain escrow contract on Solana or Sui.",
          params: { "chain": "'SOL' | 'SUI'", "seller": "PubKey", "milestones": "Array of {title, amount}", "stealth": "bool" },
          response: `{\n  "escrowId": "esc_sol_882",\n  "pda": "GhostPDA_8x92...3k1",\n  "deploymentTx": "0x..." \n}`
        },
        {
          method: "POST", path: "/v1/escrow/release", description: "Release funds to seller via AI agent signature or multi-sig.",
          params: { "escrowId": "ID", "milestoneIndex": "int" },
          response: `{\n  "status": "released",\n  "txHash": "...",\n  "amount": 1500.0\n}`
        }
      ]
    },
    {
        name: "Wormhole Bridge",
        icon: Repeat,
        endpoints: [
          {
            method: "POST", path: "/v1/bridge/transfer", description: "Initiate wGHOST burn-and-mint between Sui and Solana.",
            params: { "src": "SUI", "dst": "SOL", "amount": "25000", "recipient": "SOL_Addr" },
            response: `{\n  "vaa": "base64_encoded_vaa...",\n  "sourceTx": "0x...",\n  "status": "bridging"\n}`
          }
        ]
    },
    {
      name: "AI Agents (x402 Protocol)",
      icon: Bot,
      endpoints: [
        {
          method: "POST", path: "/v1/x402/stream/open", description: "Allocate a SUI/SOL balance for autonomous agent API consumption.",
          params: { "serviceId": "ghost-analytics-v2", "rate": "0.001", "token": "GHOST" },
          response: `{\n  "streamId": "str_402_991",\n  "accessKey": "sk_agent_...",\n  "allocated": 500.0\n}`
        },
        {
            method: "GET", path: "/v1/ai/insights/analytics", description: "Request AI-driven revenue analysis from the sovereign ledger.",
            response: `{\n  "report": "Analyzing latest trends...",\n  "anomalies": ["Whale tx detected on Sui"],\n  "forecast": "Growth Expected"\n}`
        }
      ]
    },
  ];

  return (
    <div className="w-full not-prose space-y-12">
        <div className="p-8 bg-stone-50 dark:bg-white/[0.03] rounded-[32px] border border-stone-200 dark:border-white/10 flex flex-col md:flex-row gap-8 items-center">
            <div className="w-16 h-16 bg-[#0A0B0F] dark:bg-white rounded-2xl flex items-center justify-center text-nobel-gold dark:text-[#0A0B0F] shadow-2xl shrink-0">
                <Server size={32} />
            </div>
            <div className="flex-1">
                <h3 className="text-2xl font-serif font-bold text-[#0A0B0F] dark:text-white mb-2">API Authentication</h3>
                <p className="text-stone-500 dark:text-stone-400 text-sm leading-relaxed max-w-2xl">
                    GhostPay uses <span className="text-nobel-gold font-bold">Ghost Secret Keys</span> for all server-side requests. Authenticate by including your secret key in the <code>x-api-key</code> header of every request.
                </p>
                <div className="mt-4 flex items-center gap-3">
                    <div className="px-3 py-1 bg-stone-900 dark:bg-white/10 rounded-lg text-white font-mono text-[11px] border border-white/10">
                        x-api-key: sk_live_xxxxxxxxxxxx
                    </div>
                </div>
            </div>
        </div>

       <div className="space-y-16">
           {modules.map((mod) => (
               <div key={mod.name} id={mod.name.replace(/\s/g, '-').toLowerCase()} className="scroll-mt-24">
                   <div className="flex items-center justify-between mb-8 border-b border-stone-200 dark:border-white/10 pb-5">
                       <div className="flex items-center gap-4">
                           <div className="p-2.5 bg-stone-100 dark:bg-white/[0.05] rounded-xl text-[#0A0B0F] dark:text-white shadow-inner">
                               <mod.icon size={24} strokeWidth={2} />
                           </div>
                           <h3 className="text-3xl font-serif font-bold text-[#0A0B0F] dark:text-white">{mod.name}</h3>
                       </div>
                       <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-stone-50 dark:bg-white/5 rounded-full border border-stone-100 dark:border-white/5">
                            <Code size={12} className="text-stone-400"/>
                            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.2em]">{mod.endpoints.length} Endpoints</span>
                       </div>
                   </div>
                   <div className="grid gap-4">
                       {mod.endpoints.map((ep, i) => (
                           <EndpointCard key={i} endpoint={ep} />
                       ))}
                   </div>
               </div>
           ))}
       </div>

       <div className="pt-20 mt-20 border-t border-stone-200 dark:border-white/10">
            <Card className="bg-white dark:bg-[#0A0B0F] text-[#0A0B0F] dark:text-white border-stone-200 dark:border-white/10 overflow-hidden relative">
                <div className="absolute top-0 right-0 p-8 opacity-10 blur-3xl rounded-full bg-nobel-gold w-96 h-96 -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-10 p-6 relative z-10">
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-nobel-gold rounded-full animate-pulse shadow-[0_0_10px_#C5A059]"></div>
                            <h4 className="text-2xl font-serif font-bold">Runtime Status: Optimal</h4>
                        </div>
                        <p className="text-sm text-stone-500 dark:text-stone-400 max-w-md leading-relaxed">
                            All multichain RPC gateways and ZK relay nodes are operational.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-4 w-full md:w-auto">
                        <button className="flex-1 md:flex-none px-8 py-3 rounded-xl bg-[#0A0B0F] dark:bg-white text-white dark:text-black font-bold text-sm hover:opacity-90 transition-colors shadow-2xl">Developer Portal</button>
                        <button className="flex-1 md:flex-none px-8 py-3 rounded-xl border border-stone-200 dark:border-white/20 font-bold text-sm hover:bg-stone-50 dark:hover:bg-white/10 transition-colors">Incident Log</button>
                    </div>
                </div>
            </Card>
            <div className="mt-8 text-center">
                 <p className="text-xs text-stone-500 font-mono">
                    &copy; Ghost Network. API v1.0.0 Beta. All APIs adhere to the Sovereign Privacy Protocol.
                 </p>
            </div>
       </div>
    </div>
  );
};
